import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { LoadingSpinner, PageError } from '@/components/ui';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

export default function ExportsPage() {
  const { t, i18n } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [selectedMonth, setSelectedMonth] = useState('');
  const [shouldPoll, setShouldPoll] = useState(false);

  // Fetch export jobs with dynamic polling
  const { data: exports, isLoading: isLoadingExports, error: exportsError } = useQuery({
    queryKey: ['exports'],
    queryFn: async () => {
      return apiClient.getExports();
    },
    refetchInterval: shouldPoll ? 2000 : false, // Poll every 2 seconds if needed
    refetchIntervalInBackground: true,
  });

  // Check if any exports are pending/processing
  useEffect(() => {
    const hasActiveJobs = exports?.some(
      (job: any) => job.status === 'PENDING' || job.status === 'PROCESSING'
    );
    setShouldPoll(hasActiveJobs ?? false);
  }, [exports]);

  // Generate export mutation
  const generateExportMutation = useMutation({
    mutationFn: async () => {
      if (!selectedMonth) {
        throw new Error(t('exports.selectMonthError'));
      }
      // Convert month format (YYYY-MM) to full month date range
      const [year, month] = selectedMonth.split('-');
      const startDateFull = new Date(parseInt(year), parseInt(month) - 1, 1).toISOString();
      const endDateFull = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59).toISOString();

      return apiClient.generateExport(startDateFull, endDateFull);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exports'] });
      setSelectedMonth('');
      setShouldPoll(true);
      addToast(t('exports.generateSuccess'), 'success');
    },
    onError: (error: any) => {
      addToast(extractErrorMessage(error, t('exports.generateError')), 'error');
    },
  });

  // Download export mutation
  const downloadExportMutation = useMutation({
    mutationFn: async (exportId: string) => {
      const result = await apiClient.getExportDownloadUrl(exportId);
      if (result.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
      return result;
    },
    onError: (error: any) => {
      addToast(extractErrorMessage(error, t('exports.downloadError')), 'error');
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'READY':
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'PROCESSING':
        return 'bg-blue-100 text-blue-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'READY':
      case 'COMPLETED':
        return '✓';
      case 'PROCESSING':
        return '⏳';
      case 'FAILED':
        return '✗';
      case 'PENDING':
        return '⧖';
      default:
        return '•';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'READY':
      case 'COMPLETED':
        return t('exports.statusReady');
      case 'PROCESSING':
        return t('exports.statusProcessing');
      case 'FAILED':
        return t('exports.statusFailed');
      case 'PENDING':
        return t('exports.statusPending');
      default:
        return status;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">📊 {t('exports.title')}</h1>
        <p className="text-neutral-600 mt-2">{t('exports.description')}</p>
      </div>

      {/* Generate Rapport */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
        <h2 className="text-lg font-bold text-neutral-900 mb-4">{t('exports.newReport')}</h2>

        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            {t('exports.reportDescription')}
          </p>

          <div className="w-full sm:max-w-xs">
            <div>
              <label className="block text-sm font-medium text-neutral-900 mb-2">{t('exports.chooseMonth')}</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <button
            onClick={() => generateExportMutation.mutate()}
            disabled={!selectedMonth || generateExportMutation.isPending}
            className="w-full py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {generateExportMutation.isPending ? t('exports.loading') : `📥 ${t('exports.export')}`}
          </button>
        </div>
      </div>

      {/* Rapport Jobs List */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h2 className="text-lg font-bold text-neutral-900 mb-4">{t('exports.recentReports')}</h2>

        {isLoadingExports ? (
          <LoadingSpinner message={t('exports.loadingReports')} />
        ) : exportsError ? (
          <PageError message={t('exports.loadError')} />
        ) : exports?.length === 0 ? (
          <div className="text-center text-neutral-500 py-8">
            <div className="text-4xl mb-2">📭</div>
            <div>{t('exports.noReports')}</div>
          </div>
        ) : (
          <div className="space-y-3">
            {exports?.map((exportJob: any) => (
              <div
                key={exportJob.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-neutral-200 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-start sm:items-center gap-4 flex-1">
                  <div className="text-2xl">📄</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-neutral-900">
                      {new Date(exportJob.periodStart).toLocaleDateString(i18n.language)} -{' '}
                      {new Date(exportJob.periodEnd).toLocaleDateString(i18n.language)}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {t('exports.created')} {new Date(exportJob.createdAt).toLocaleString(i18n.language)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(exportJob.status)}`}>
                    {exportJob.status === 'PROCESSING' ? (
                      <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin inline-block" />
                    ) : (
                      getStatusIcon(exportJob.status)
                    )} {getStatusLabel(exportJob.status)}
                  </span>

                  {(exportJob.status === 'READY' || exportJob.status === 'COMPLETED') && (
                    <button
                      onClick={() => downloadExportMutation.mutate(exportJob.id)}
                      disabled={downloadExportMutation.isPending}
                      className="w-full sm:w-auto px-4 py-2 text-sm rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 font-medium transition-colors"
                    >
                      {downloadExportMutation.isPending ? t('exports.downloading') : t('exports.download')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-bold text-blue-900 mb-2">ℹ️ {t('exports.infoTitle')}</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>✓ {t('exports.info1')}</li>
          <li>✓ {t('exports.info2')}</li>
          <li>✓ {t('exports.info3')}</li>
          <li>✓ {t('exports.info4')}</li>
          <li>✓ {t('exports.info5')}</li>
          <li>✓ {t('exports.format')}</li>
          <li>✓ {t('exports.info7')}</li>
          <li>✓ {t('exports.info8')}</li>
        </ul>
      </div>
    </div>
  );
}
